import { collection } from 'infra/collection'
import {
  ArchiveInventoryItemRequest,
  WorkspaceWithInventoryItems as WorkspaceWithInventoryItemsProto,
  ListInventoryItemsRequest,
  CreateInventoryItemRequest,
  UpdateInventoryItemRequest,
  InventoryItemUpdateOptions,
  ListWorkspacesWithInventoryItemsRequest,
  InventoryItemPriceType,
  ListInventoryItemsForProvidersRequest,
  InventoryItemSupportingItem as InventoryItemSupportingItemProto,
  InventoryItemDocumentSignature as InventoryItemDocumentSignatureItemProto,
  SetProVisibilityRequest,
  InventoryItemProVisibility,
  UnarchiveInventoryItemRequest,
  GetManyInventoryItemsRequest,
  InventoryItemTypeSpecification as InventoryItemTypeSpecificationProto,
} from '@taxfyle/api-internal/internal/inventory_item_pb'
import InventoryItem, {
  InventoryItemConsultationTypeSpecification,
  InventoryItemDocumentSignatureItem,
  InventoryItemPricing,
  InventoryItemSupportingItem,
  InventoryItemSupportingItemSpecTypes,
  InventoryItemType,
  InventoryItemTypeSpecification,
} from './inventory/InventoryItem'
import WorkspaceStore, { PrimeWorkspace } from 'iam/WorkspaceStore'
import { InventoryAPI } from './API'
import { PrimeUser } from 'iam/UserStore'
import { numberToDecimal } from '../utils/grpc-util'
import { FieldMask } from 'google-protobuf/google/protobuf/field_mask_pb'
import { Id } from 'types/definitions'
import WorkspaceWithInventoryItems from './inventory/WorkspaceWithInventoryItems'
import { mapInventoryItemProtoToDTO } from './dtos/inventoryItemGrpcDtoMapper'
import { InventoryItemDto } from './dtos/inventory-item'
import { PrimeDocumentTypeByShortId } from '../documents/DocumentTypeStore'
import { match } from 'utils/pattern-match-util'
import { InventoryItemPricingValues } from './inventory/views/BaseInventoryItemDialog/inventory-item-pricing-editor'
import { action, makeObservable, observable } from 'mobx'
import { areInventoryItemVersionsEquivalent } from './inventory/views/BaseInventoryItemDialog/BaseInventoryItemViewModel'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'

export interface InventorySearchQuery {
  workspaceId?: string
  searchText?: string
}

export default class InventoryStore {
  @observable totalItemCountPerWorkspace = new Map<string, number>()

  /**
   * Inventory items collection.
   */
  inventoryItems = collection<InventoryItemDto, InventoryItem>({
    create: () =>
      new InventoryItem(
        this.primeWorkspace,
        this.primeUser,
        this.primeDocumentTypeByShortId
      ),
  })

  workspacesWithInventoryItems = collection<
    WorkspaceWithInventoryItemsProto.AsObject,
    WorkspaceWithInventoryItems
  >({
    create: () => new WorkspaceWithInventoryItems(this.workspaceStore),
    getDataId: (json) => json.workspaceId,
    getModelId: (model) => model.workspaceId,
  })

  constructor(
    private inventoryAPI: InventoryAPI,
    private workspaceStore: WorkspaceStore,
    private primeWorkspace: PrimeWorkspace,
    private primeUser: PrimeUser,
    private primeDocumentTypeByShortId: PrimeDocumentTypeByShortId
  ) {
    makeObservable(this)
  }

  /**
   * Fetch inventory items from api
   * @param workspaceId
   * @returns
   */
  async fetchInventoryItems(
    workspaceId: Id,
    limit: number,
    searchText?: string,
    pageToken?: string,
    filter?: string,
    orderBy?: string
  ) {
    const request = new ListInventoryItemsRequest()

    if (workspaceId) {
      request.setWorkspaceId(workspaceId)
    }
    if (limit) {
      request.setPageSize(limit)
    }
    if (pageToken) {
      request.setPageToken(pageToken)
    }
    if (filter) {
      request.setFilter(filter)
    }
    if (orderBy) {
      request.setOrderBy(orderBy)
    }
    if (searchText) {
      request.setSearchText(searchText)
    }

    const result = await this.inventoryAPI.listInventoryItems(request)

    this.setTotalItemCountPerWorkspace(
      workspaceId ?? 'global',
      result.getTotalSize()
    )

    return {
      data: this.inventoryItems.set(
        result
          .getInventoryItemsList()
          .map((op) => mapInventoryItemProtoToDTO(op))
      ),
      total: result.getTotalSize(),
      cursor: result.getNextPageToken(),
    }
  }

  async fetchInventoryItemsForProvider(
    workspaceId: Id,
    limit: number,
    searchText?: string,
    pageToken?: string,
    filter?: string,
    orderBy?: string
  ) {
    const request = new ListInventoryItemsForProvidersRequest()

    if (workspaceId) {
      request.setWorkspaceId(workspaceId)
    }
    if (limit) {
      request.setPageSize(limit)
    }
    if (pageToken) {
      request.setPageToken(pageToken)
    }
    if (filter) {
      request.setFilter(filter)
    }
    if (orderBy) {
      request.setOrderBy(orderBy)
    }
    if (searchText) {
      request.setSearchText(searchText)
    }

    const result = await this.inventoryAPI.listInventoryItemsForProviders(
      request
    )

    return {
      data: result.getInventoryItemsList().map((op) => op.toObject()),
      total: result.getInventoryItemsList().length,
      cursor: result.getNextPageToken(),
    }
  }

  /**
   * Fetch workspaces with inventory items
   */
  async fetchWorkspacesWithInventoryItems() {
    const request = new ListWorkspacesWithInventoryItemsRequest()

    const result = await this.inventoryAPI.listWorkspacesWithInventoryItems(
      request
    )

    this.workspacesWithInventoryItems.reset(
      result.getWorkspacesList().map((op) => op.toObject())
    )
  }

  async create(payload: CreateInventoryItemParams) {
    const supportingItems = this.mapInventoryItemSupportingItems(payload)
    const documentSignatureItems =
      this.mapInventoryItemDocumentSignatureItems(payload)
    const typeSpecification = this.mapInventoryItemTypeSpecification(
      payload.typeSpecification
    )

    const request = new CreateInventoryItemRequest()
      .setName(payload.name)
      .setDescription(payload.description)
      .setCourtesyAmount(payload.courtesyAmount)
      .setPrice(numberToDecimal(toClientPrice(payload.pricing)))
      .setProPrice(numberToDecimal(toProPayout(payload.pricing)))
      .setPriceType(toPriceType(payload.pricing))
      .setSupportingItemsList(supportingItems)
      .setDocumentSignaturesList(documentSignatureItems)
      .setTypeSpecification(typeSpecification)

    if (payload.workspace) {
      request.setWorkspaceId(payload.workspace)
    }

    const item = await this.inventoryAPI.createInventoryItem(request)

    if (payload.hideFromPro) {
      const setHiddenFromProRequest = new SetProVisibilityRequest()
        .setId(item.getId())
        .setVisibility(InventoryItemProVisibility.HIDDEN)

      await this.inventoryAPI.setProVisibilityForInventoryItem(
        setHiddenFromProRequest
      )
    }
  }

  async update(id: string, payload: UpdateInventoryItemParams) {
    const inventoryItemToBeUpdated = this.inventoryItems.find(
      (i) => i.id === id
    )

    if (
      !areInventoryItemVersionsEquivalent(inventoryItemToBeUpdated!, payload)
    ) {
      const supportingItems = this.mapInventoryItemSupportingItems(payload)
      const documentSignatureItems =
        this.mapInventoryItemDocumentSignatureItems(payload)
      const typeSpecification =
        payload.typeSpecification !== null
          ? this.mapInventoryItemTypeSpecification(payload.typeSpecification)
          : undefined

      const options = new InventoryItemUpdateOptions()
        .setName(payload.name)
        .setDescription(payload.description)
        .setCourtesyAmount(payload.courtesyAmount)
        .setPrice(numberToDecimal(toClientPrice(payload.pricing)))
        .setProPrice(numberToDecimal(toProPayout(payload.pricing)))
        .setSupportingItemsList(supportingItems)
        .setDocumentSignaturesList(documentSignatureItems)
        .setTypeSpecification(typeSpecification)

      const fieldMask = new FieldMask().setPathsList([
        'name',
        'description',
        'courtesy_amount',
        'price',
        'pro_price',
        'supporting_items',
        'type_specification',
        'document_signatures',
      ])

      const request = new UpdateInventoryItemRequest()
        .setId(id)
        .setOptions(options)
        .setUpdateMask(fieldMask)

      await this.inventoryAPI.updateInventoryItem(request)
    }

    if (payload.proVisibility !== inventoryItemToBeUpdated!.proVisibility) {
      const setHiddenFromProRequest = new SetProVisibilityRequest()
        .setId(id)
        .setVisibility(payload.proVisibility)

      await this.inventoryAPI.setProVisibilityForInventoryItem(
        setHiddenFromProRequest
      )
    }
  }

  async override(payload: OverrideInventoryItemParams) {
    const supportingItems = this.mapInventoryItemSupportingItems(payload)
    const documentSignatureItems =
      this.mapInventoryItemDocumentSignatureItems(payload)
    const typeSpecification = this.mapInventoryItemTypeSpecification(
      payload.typeSpecification
    )

    const request = new CreateInventoryItemRequest()
      .setName(payload.name)
      .setDescription(payload.description)
      .setCourtesyAmount(payload.courtesyAmount)
      .setPrice(numberToDecimal(toClientPrice(payload.pricing)))
      .setProPrice(numberToDecimal(toProPayout(payload.pricing)))
      .setPriceType(toPriceType(payload.pricing))
      .setWorkspaceId(payload.workspace)
      .setRootId(payload.rootId)
      .setSupportingItemsList(supportingItems)
      .setDocumentSignaturesList(documentSignatureItems)
      .setTypeSpecification(typeSpecification)

    const createdItem = await this.inventoryAPI.createInventoryItem(request)

    if (payload.hideFromPro) {
      const setHiddenFromProRequest = new SetProVisibilityRequest()
        .setId(createdItem.getId())
        .setVisibility(InventoryItemProVisibility.HIDDEN)

      await this.inventoryAPI.setProVisibilityForInventoryItem(
        setHiddenFromProRequest
      )
    }

    const result = await this.inventoryAPI.listWorkspacesWithInventoryItems(
      request
    )

    this.workspacesWithInventoryItems.set(
      result.getWorkspacesList().map((op) => op.toObject())
    )
  }

  private mapInventoryItemSupportingItems = (
    payload:
      | CreateInventoryItemParams
      | UpdateInventoryItemParams
      | OverrideInventoryItemParams
  ) =>
    payload.supportingItems.map((item) => {
      const supportingItem = new InventoryItemSupportingItemProto()
      const spec = new InventoryItemSupportingItemProto.Spec()

      switch (item.spec.type) {
        case InventoryItemSupportingItemSpecTypes.DOCUMENT:
          spec.setDocument(
            new InventoryItemSupportingItemProto.Spec.Document().setTypeShortId(
              Id.assert(item.spec.documentType.shortId)
            )
          )
          break
        default:
          throw new Error("Unsupported supporting item's spec type")
      }

      supportingItem.setSpec(spec)
      supportingItem.setRequired(item.required)
      return supportingItem
    })

  private mapInventoryItemDocumentSignatureItems = (
    payload:
      | CreateInventoryItemParams
      | UpdateInventoryItemParams
      | OverrideInventoryItemParams
  ) =>
    payload.documentSignatureItems.map((item) =>
      new InventoryItemDocumentSignatureItemProto().setDocumentTypeShortId(
        Id.assert(item.documentType.shortId)
      )
    )

  private mapInventoryItemTypeSpecification(
    spec: InventoryItemTypeSpecification
  ): InventoryItemTypeSpecificationProto {
    return match(spec.type, {
      [InventoryItemType.Default]: () =>
        new InventoryItemTypeSpecificationProto().setSpec(
          new InventoryItemTypeSpecificationProto.TypeOptions().setDefault(
            new Empty()
          )
        ),
      [InventoryItemType.Bookkeeping]: () =>
        new InventoryItemTypeSpecificationProto().setSpec(
          new InventoryItemTypeSpecificationProto.TypeOptions().setBookkeeping(
            new Empty()
          )
        ),
      [InventoryItemType.Consultation]: () =>
        new InventoryItemTypeSpecificationProto().setSpec(
          new InventoryItemTypeSpecificationProto.TypeOptions().setConsultation(
            new InventoryItemTypeSpecificationProto.TypeOptions.Consultation().setLengthInMinutes(
              (spec as InventoryItemConsultationTypeSpecification)
                .lengthInMinutes
            )
          )
        ),
    })
  }

  async archive(id: string) {
    const request = new ArchiveInventoryItemRequest().setId(id)

    await this.inventoryAPI.archiveInventoryItem(request)
  }

  async unarchive(id: string) {
    const request = new UnarchiveInventoryItemRequest().setId(id)

    await this.inventoryAPI.unarchiveInventoryItem(request)
  }

  async getManyInventoryItemsByIds(ids: string[], workspaceId: string) {
    const request = new GetManyInventoryItemsRequest()
      .setIdsList(ids)
      .setWorkspaceId(workspaceId)

    const result = await this.inventoryAPI.getManyInventoryItems(request)

    return this.inventoryItems.set(
      result
        .getInventoryItemsList()
        .filter((op) => op.getItem())
        .map((op) => {
          return mapInventoryItemProtoToDTO(op.getItem()!)
        })
    )
  }

  @action.bound
  setTotalItemCountPerWorkspace(workspaceId: string, count: number) {
    this.totalItemCountPerWorkspace.set(workspaceId, count)
  }
}

export interface CreateInventoryItemParams {
  name: string
  description: string
  courtesyAmount: number
  pricing: InventoryItemPricingValues
  workspace?: string
  supportingItems: InventoryItemSupportingItem[]
  documentSignatureItems: InventoryItemDocumentSignatureItem[]
  hideFromPro: boolean
  typeSpecification: InventoryItemTypeSpecification
}

export interface UpdateInventoryItemParams {
  name: string
  description: string
  courtesyAmount: number
  pricing: InventoryItemPricingValues
  supportingItems: InventoryItemSupportingItem[]
  documentSignatureItems: InventoryItemDocumentSignatureItem[]
  proVisibility: InventoryItemProVisibility
  typeSpecification: InventoryItemTypeSpecification | null
}

export interface OverrideInventoryItemParams {
  name: string
  description: string
  courtesyAmount: number
  pricing: InventoryItemPricingValues
  workspace: string
  rootId: string
  supportingItems: InventoryItemSupportingItem[]
  documentSignatureItems: InventoryItemDocumentSignatureItem[]
  hideFromPro: boolean
  typeSpecification: InventoryItemTypeSpecification
}

/**
 * Maps from the Editor Values representation to a decimal.
 */
export function toClientPrice(pricing: InventoryItemPricingValues) {
  switch (pricing.type) {
    case InventoryItemPricing.Fee:
      return pricing.fee
    case InventoryItemPricing.Flat:
      return pricing.clientPrice
    case InventoryItemPricing.Markup:
      return pricing.clientMarkupRate / 100
    case InventoryItemPricing.Markdown:
      return pricing.clientMarkdownRate / 100
  }
}

/**
 * Maps from the Editor Values representation to a decimal.
 */
export function toProPayout(pricing: InventoryItemPricingValues) {
  switch (pricing.type) {
    case InventoryItemPricing.Fee:
      return 0
    case InventoryItemPricing.Flat:
      return pricing.proPayoutAmount
    case InventoryItemPricing.Markup:
      return pricing.proPayoutMarkupRate / 100
    case InventoryItemPricing.Markdown:
      return pricing.proPayoutMarkdownRate / 100
  }
}

/**
 * Maps from the Editor Values representation ot the proto enum.
 */
export function toPriceType(pricing: InventoryItemPricingValues) {
  return match(pricing.type, {
    [InventoryItemPricing.Fee]: () => InventoryItemPriceType.FEE,
    [InventoryItemPricing.Flat]: () => InventoryItemPriceType.FLAT,
    [InventoryItemPricing.Markdown]: () => InventoryItemPriceType.MARKDOWN,
    [InventoryItemPricing.Markup]: () => InventoryItemPriceType.MARKUP,
  })
}
